import { store } from '@/store';
import {
  incrementPartNumber,
  setRecordingError,
  startRecording,
  stopRecording,
} from '@/store/slices/recordingSlice';
import { AiScribeJobUtilsService } from './ai-scribe-job-utils.service';
import { AiScribeCompleteResponse, aiScribeService } from './ai-scribe-service';
import { AudioProcessorService } from './audio-processor';
import { UploadScheduler } from './upload-scheduler';

export class AIScribeRecordingService {
  private audioProcessor: AudioProcessorService;
  private stream: MediaStream | null = null;
  private isAudioProcessorReady: boolean = false;
  private uploadScheduler: UploadScheduler | null = null;

  constructor() {
    this.audioProcessor = new AudioProcessorService();
  }

  /**
   * Get the audio processor instance for waveform connection
   */
  getAudioProcessor(): AudioProcessorService {
    return this.audioProcessor;
  }

  /**
   * Check if the audio processor is ready for audio level subscriptions
   */
  getAudioProcessorReady(): boolean {
    return (
      this.isAudioProcessorReady && this.audioProcessor.isReadyForAudioLevels()
    );
  }

  /**
   * Check if audio processing is active and receiving data
   */
  isProcessingAudio(): boolean {
    return this.audioProcessor.isProcessingAudio();
  }

  /**
   * Get audio buffer length for debugging
   */
  getAudioBufferLength(): number {
    return this.audioProcessor.getAudioBufferLength();
  }

  /**
   * Check if audio processor is properly initialized
   */
  isAudioProcessorInitialized(): boolean {
    return this.audioProcessor.isInitialized();
  }

  /**
   * Force cleanup - used when navigating away or unmounting
   * Stops everything immediately without uploading
   */
  forceCleanup(): void {
    console.log('🧹 Force cleanup - stopping all recording activities');

    // Stop the upload scheduler
    if (this.uploadScheduler) {
      this.uploadScheduler.stop();
      this.uploadScheduler = null;
    }

    // Stop and cleanup audio processor
    if (this.audioProcessor) {
      this.audioProcessor.cleanup();
    }

    // Stop media stream (releases microphone)
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
        console.log('🎤 Stopped media track:', track.kind);
      });
      this.stream = null;
    }

    // Reset flags
    this.isAudioProcessorReady = false;

    console.log('✅ Force cleanup complete - all resources released');
  }

  /**
   * Start WAV recording with configurable interval
   */
  async startWavRecording(
    appointmentId: string,
    patientId: string,
    locationId: string,
    partNumber: number,
    createdBy: string
  ): Promise<void> {
    const state = store.getState();
    const { isRecording, isStopped } = state.recording;

    if (isRecording) {
      console.warn('Recording is already in progress');
      return;
    }

    if (isStopped) {
      console.warn(
        'Recording was stopped. Please refresh the page to start a new recording.'
      );
      return;
    }

    try {
      // Initialize audio stream and context first
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Initialize audio processor with config
      await this.audioProcessor.initialize(this.stream, {
        bufferSize: state.recording.config.bufferSize,
        uploadInterval: state.recording.config.uploadInterval,
        silenceThreshold: state.recording.config.silenceThreshold,
        silenceDurationThreshold:
          state.recording.config.silenceDurationThreshold,
      });

      // Mark audio processor as ready immediately after initialization
      this.isAudioProcessorReady = true;

      // Start processing audio
      await this.audioProcessor.startProcessing();

      // Small delay to ensure audio processor is fully ready before Redux state update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Start the upload scheduler
      this.uploadScheduler = new UploadScheduler(
        this.audioProcessor,
        createdBy
      );
      this.uploadScheduler.start();

      // Update Redux state only after everything is successfully initialized
      store.dispatch(
        startRecording({
          appointmentId,
          patientId,
          locationId,
          partNumber,
        })
      );
    } catch (error) {
      console.error('❌ Error starting WAV recording:', error);

      // Cleanup on error
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      this.audioProcessor.cleanup();
      this.isAudioProcessorReady = false;
      this.uploadScheduler?.stop();

      store.dispatch(
        setRecordingError(
          error instanceof Error ? error.message : 'Failed to start recording'
        )
      );
      throw error;
    }
  }

  /**
   * Stop the WAV recording
   */
  async stopWavRecording(
    patientId: string,
    loggedInUserId: string,
    shouldCallAPI: boolean = false
  ): Promise<AiScribeCompleteResponse | void> {
    // Instead, check if we actually have an active recording service:
    // This is a safety check to prevent stopping a recording that never started.
    if (!this.audioProcessor || !this.isAudioProcessorReady) {
      console.warn('No active recording to stop');
      return;
    }

    // Immediately stop waveform animation and controls
    this.uploadScheduler?.stop();

    try {
      const state = store.getState();
      const { appointmentId } = state.recording;
      if (!appointmentId) {
        throw new Error('No appointment ID available to stop recording.');
      }

      // Get the current part number for the final chunk
      const partNumber = state.recording.currentPartNumber;

      // Get the final buffer BEFORE cleaning up the processor
      const finalBufferResult = await this.audioProcessor.getCurrentBuffer();

      // Now cleanup
      this.audioProcessor.cleanup();
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
      }

      // Increment part number immediately when starting final upload
      store.dispatch(incrementPartNumber());
      console.log(
        `📈 Incremented part number to: ${
          partNumber + 1
        } (final upload started)`
      );

      // Upload final chunk with isFinalPart=true

      // Dispatch stopRecording to update Redux state
      store.dispatch(stopRecording());

      if (finalBufferResult.blob.size > 0) {
        const selectedAppointment =
          state.data?.appointments?.selectedAppointment;
        const scribeJobId = selectedAppointment?.scribeJobId;

        if (scribeJobId) {
          const finalUploadResult = await this.uploadScheduler?.uploadWavChunk(
            finalBufferResult.blob,
            partNumber,
            loggedInUserId,
            scribeJobId,
            true, // isFinalPart = true for the final chunk
            finalBufferResult.durationSeconds
          );

          console.log(
            '✅ Final chunk uploaded successfully:',
            finalUploadResult
          );
        } else {
          console.error('❌ Missing scribeJobId for final chunk upload');
        }
      }

      if (shouldCallAPI) {
        const selectedAppointment =
          state.data?.appointments?.selectedAppointment;
        const scribeJobId = selectedAppointment?.scribeJobId;
        if (scribeJobId) {
          // Fire-and-forget: don't block the stop flow waiting for server processing.
          // Results arrive via Supabase Realtime (ev_scribe_parts updates).
          this.completeRecording(scribeJobId)
            .then((result) => {
              console.log('✅ complete-recording resolved in background:', result);
            })
            .catch((error) => {
              console.warn(
                '⚠️ complete-recording failed in background (server may still process):',
                error
              );
            });
        }
      }

      this.cleanup();
    } catch (error) {
      console.error('Error stopping recording:', error);
      store.dispatch(
        setRecordingError(
          error instanceof Error ? error.message : 'Failed to stop recording'
        )
      );
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.audioProcessor.cleanup();
    this.isAudioProcessorReady = false;
    this.uploadScheduler = null;
  }

  /**
   * Pause the WAV recording
   */
  async pauseWavRecording(patientId: string): Promise<void> {
    const state = store.getState();
    const { isRecording, isPaused, isStopped } = state.recording;

    if (isRecording && !isPaused && !isStopped) {
      try {
        const { appointmentId, locationId } = state.recording;
        if (!appointmentId) {
          console.error('No appointment ID available for pause');
          return;
        }

        // Stop the scheduler
        this.uploadScheduler?.stop();

        // Get the current part number for the pause chunk
        const partNumber = state.recording.currentPartNumber;

        const audioBufferResult = await this.audioProcessor.getCurrentBuffer();
        // Increment part number immediately when starting pause upload
        store.dispatch(incrementPartNumber());
        console.log(
          `📈 Incremented part number to: ${
            partNumber + 1
          } (pause upload started)`
        );

        if (audioBufferResult.blob.size > 0) {
          const selectedAppointment =
            state.data?.appointments?.selectedAppointment;
          const scribeJobId = selectedAppointment?.scribeJobId;
          if (!scribeJobId) {
            console.error('[AIScribe] Cannot pause: scribeJobId is missing');
            return;
          }

          // Update job status to PAUSED in database
          await AiScribeJobUtilsService.updateScribeJobStatus(
            scribeJobId,
            'PAUSED'
          );
          console.log('[AIScribe] ✅ Job status updated to PAUSED');

          this.uploadScheduler
            ?.uploadWavChunk(
              audioBufferResult.blob,
              partNumber,
              'Unknown User', // createdBy is not needed for pause
              scribeJobId,
              false, // isFinalPart = false for pause chunks
              audioBufferResult.durationSeconds
            )
            .then(() => {
              console.log('✅ Pause chunk uploaded successfully');
            })
            .catch((error) => {
              console.error('Pause upload failed:', error);
              // Part number is already incremented
            });
        }

        this.audioProcessor.pauseProcessing();
      } catch (error) {
        console.error('Error uploading final chunk before pause:', error);
      }
    }
  }

  /**
   * Resume the WAV recording
   */
  resumeWavRecording(patientId: string): void {
    const state = store.getState();
    const { isRecording, isPaused, isStopped } = state.recording;

    if (isRecording && isPaused && !isStopped) {
      const { appointmentId } = state.recording;

      if (!appointmentId) {
        console.error('No appointment ID available for resume');
        return;
      }

      this.audioProcessor.resumeProcessing();
      this.uploadScheduler?.start();
    }
  }

  /**
   * Stop the section-based WAV recording and upload to generate section JSON
   */
  async stopSectionWavRecording(
    patientId: string,
    scribeJobId: string,
    sectionTag: string,
    loggedInUserId: string,
    dxList?: any
  ): Promise<{ partId?: string; partNumber?: number }> {
    // Check if we have an active recording service
    if (!this.audioProcessor || !this.isAudioProcessorReady) {
      console.warn('No active recording to stop');
      throw new Error('No active recording to stop');
    }

    // Immediately stop waveform animation and controls
    this.uploadScheduler?.stop();

    try {
      const state = store.getState();
      const { appointmentId } = state.recording;
      if (!appointmentId) {
        throw new Error('No appointment ID available to stop recording.');
      }

      // Get the current part number for the final chunk
      const partNumber = state.recording.currentPartNumber;

      // Get the final buffer BEFORE cleaning up the processor
      const finalBufferResult = await this.audioProcessor.getCurrentBuffer();

      // Now cleanup
      this.audioProcessor.cleanup();
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
      }

      // Increment part number immediately when starting final upload
      store.dispatch(incrementPartNumber());
      console.log(
        `📈 Incremented part number to: ${
          partNumber + 1
        } (final section upload started)`
      );

      // Dispatch stopRecording to update Redux state
      store.dispatch(stopRecording());

      let uploadResult: { partId?: string; partNumber?: number } = {};
      // Upload final chunk with isFinalPart=true using section endpoint
      if (finalBufferResult.blob.size > 0) {
        uploadResult =
          (await this.uploadScheduler?.uploadSectionWavChunk(
            finalBufferResult.blob,
            partNumber,
            loggedInUserId,
            scribeJobId,
            sectionTag,
            true, // isFinalPart = true for the final chunk
            finalBufferResult.durationSeconds,
            dxList
          )) || {};

        console.log(
          '✅ Final section chunk uploaded successfully:',
          uploadResult
        );
      }

      this.cleanup();
      return uploadResult;
    } catch (error) {
      console.error('Error stopping section recording:', error);
      store.dispatch(
        setRecordingError(
          error instanceof Error
            ? error.message
            : 'Failed to stop section recording'
        )
      );
      throw error;
    }
  }

  /**
   * Pause the section-based WAV recording
   */
  async pauseSectionWavRecording(
    patientId: string,
    scribeJobId: string,
    sectionTag: string
  ): Promise<void> {
    const state = store.getState();
    const { isRecording, isPaused, isStopped } = state.recording;

    if (isRecording && !isPaused && !isStopped) {
      try {
        const { appointmentId } = state.recording;
        if (!appointmentId) {
          console.error('No appointment ID available for pause');
          return;
        }

        // Stop the scheduler
        this.uploadScheduler?.stop();

        // Get the current part number for the pause chunk
        const partNumber = state.recording.currentPartNumber;

        const audioBufferResult = await this.audioProcessor.getCurrentBuffer();

        // Increment part number immediately when starting pause upload
        store.dispatch(incrementPartNumber());
        console.log(
          `📈 Incremented part number to: ${
            partNumber + 1
          } (pause section upload started)`
        );

        // Update job status to PAUSED in database
        await AiScribeJobUtilsService.updateScribeJobStatus(
          scribeJobId,
          'PAUSED'
        );
        console.log('[AIScribe] ✅ Job status updated to PAUSED');

        if (audioBufferResult.blob.size > 0) {
          this.uploadScheduler
            ?.uploadSectionWavChunk(
              audioBufferResult.blob,
              partNumber,
              'Unknown User', // createdBy is not needed for pause
              scribeJobId,
              sectionTag,
              false, // isFinalPart = false for pause chunks
              audioBufferResult.durationSeconds
            )
            .then(() => {
              console.log('✅ Pause section chunk uploaded successfully');
            })
            .catch((error) => {
              console.error('Pause section upload failed:', error);
              // Part number is already incremented
            });
        }

        this.audioProcessor.pauseProcessing();
      } catch (error) {
        console.error('Error uploading final chunk before pause:', error);
      }
    }
  }

  /**
   * Complete recording using AI Scribe service
   */
  private async completeRecording(
    scribeJobId: string
  ): Promise<AiScribeCompleteResponse | void> {
    try {
      const response = await aiScribeService.completeRecording({
        scribeJobId,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to complete recording');
      }

      console.log('Recording completed successfully:', response.data);

      // If we received an audioUrl in the response, update the appointment with it
      if (response.data?.audioUrl) {
        const state = store.getState();
        const selectedAppointment =
          state.data?.appointments?.selectedAppointment;

        if (selectedAppointment) {
          const { updateAppointmentAudioUrl } = await import(
            '@/store/slices/dataSlice'
          );
          store.dispatch(
            updateAppointmentAudioUrl({
              appointmentId: selectedAppointment.appointmentId,
              audioUrl: response.data.audioUrl,
              transcriptUrl: response.data.transcriptUrl || '',
            })
          );
          console.log(
            '✅ Updated appointment with audioUrl:',
            response.data.audioUrl
          );
          console.log(
            '✅ Updated appointment with transcriptUrl:',
            response.data.transcriptUrl
          );
        } else {
          console.warn('⚠️ No selected appointment to update with audioUrl');
        }
      }

      return response.data;
    } catch (error) {
      console.error('Failed to complete recording:', error);
      throw error;
    }
  }
}
